import { Media, MessagePayload } from '@zonofi/common/schema/message/inferface';

export interface ConversationListRequest {
  sellerWorkspaceId: string;
  limit: number;
  lastEvaluatedTimeToken?: string;
  assigneeId?: string;
  category?: string;
  tags?: string[];
  teamMemberIds?: string[];
  startDate?: string; // Start date for filtering (ISO string)
  endDate?: string; // End date for filtering (ISO string)
  assignedToMe?: boolean;
  lastEvaluatedConversationThreadId?: string;
  customerId?: string;
  conversationId?: string;
  status?: string;
  channelTypes?: string[];
  channelSubTypes?: string[];
  queryTypes?: string[];
}

export interface ConversationListResponse {
  success: boolean;
  data: ConversationData[];
  message: string;
  pagination: ConversationPagination;
}

export interface ConversationThreadActionsRequest {
  sellerWorkspaceId: string;
  conversationThreadId: string;
}

export interface ConversationThreadActionsResponse {
  actions: string[];
}

export interface InternalNoteLastKey {
  sid: string;
  customerId_isInternalNote: string;
  timetoken: string;
}

export interface InternalNotesApiRequest extends Partial<InternalNoteLastKey> {
  customerId: string;
  workspaceId: string;
  limit?: number;
}

export interface InternalNoteData {
  isInternalNote: boolean;
  message: InternalNoteMessage;
  summary?: string | null;
  sid?: string;
  lastMessage?: string;
  lastMessageAt?: string;
  conversationId?: string;
  stats: MsgStats;
  timetoken: string;
}

export interface InternalNoteMessage {
  text: string;
  actionBy?: {
    email: string;
    firstName: string;
  };
  media?: Media[];
}

export interface InternalNotesApiResponse {
  success: boolean;
  data: { messages: InternalNoteData[]; lastKey: InternalNoteLastKey };
  message: string;
}

export interface ConversationData {
  customerPhone?: string;
  customerName?: string;
  metadata?: ConversationMetadata;
  lastMessageAt?: string;
  status?: string;
  isAssigned?: boolean;
  createdAt: string;
  conversationThreadId?: string;
  workspaceId: string;
  customerId: string | null;
  chatId?: string;
  isWidgetChat?: boolean;
  isRegisteredCustomer?: boolean;
  windowStartTime?: string;
  windowEndTime?: string;
  startMessageTimetoken?: string;
  endMessageTimetoken?: string;
  description?: string;
  channelType?: string;
  channelSubType?: string | null;
  queryType?: string;
  channelName?: string;
  isGroup?: boolean;
  channelId?: string;
  title?: string;
  updatedAt?: string;
  assignedTo?: ConversationAssignee[];
  widgetChat?: {
    customerLocation: string;
    isWidgetChat: boolean;
    referralSource: string;
    registrationStatus: string;
    sessionId: string;
  };
  customerLifeCyclePaths?: string[];
}

export interface ConversationMetadata {
  tags?: string[];
  customerType?: 'new' | 'new_lead';
  campaignName?: string;
  status?: string;
  assignmentStats?: ConversationAssignmentStats;
  extractedCustomers?: ExtractedConversationCustomer[];
}

export interface ConversationPagination {
  hasMore: boolean;
  nextCursor?: NextCursor;
  total: number;
  lastEvaluatedTimeToken?: string | null;
  lastEvaluatedConversationThreadId?: string | null;
}

interface NextCursor {
  lastMessageAt: string;
  startKey: string;
}

export interface ConversationAssignmentStats {
  semantic: number;
  rfor: number;
  llm: number;
}

export interface ExtractedConversationCustomer {
  name: string;
  customerId: string;
}

export interface UpdateConversationStatusRequest {
  sellerWorkspaceId: string;
  conversationId: string;
  status: string;
}

export interface UpdateConversationAssigneeRequest {
  teamMemberId: string;
  conversationId: string;
  type?: UpdateConversationAssigneeType;
  workspaceId: string;
}

export enum UpdateConversationAssigneeType {
  ASSIGN = 'assign',
  WATCHER = 'watcher',
}

export interface ConversationAssignee {
  teamMemberName: string;
  teamMemberId: string;
  role: ConversationAssigneType;
}

export enum ConversationAssigneType {
  VIEWER = 'viewer',
  MEMBER = 'member',
}

export const TeamMemerRoleMap = {
  [ConversationAssigneType.VIEWER]: UpdateConversationAssigneeType.WATCHER,
  [ConversationAssigneType.MEMBER]: UpdateConversationAssigneeType.ASSIGN,
};

export const TeamMemberRoleDrawerDisplay: Record<
  UpdateConversationAssigneeType,
  string
> = {
  [UpdateConversationAssigneeType.ASSIGN]: 'Owner',
  [UpdateConversationAssigneeType.WATCHER]: 'Watcher',
};

export interface UpdateConversationResponse {
  data: ConversationData;
  message: string;
  success: boolean;
}

export interface ConversationStatusResponse {
  statuses: Status[];
  workspaceId: string;
  totalCount: number;
}

export enum ConversationStatusGroup {
  OPEN = 'Open',
  INPROGRESS = 'InProgress',
  CLOSED = 'Close',
}

export enum ConversationStatusAssigned {
  ASSIGNED = 'Assigned',
}

interface Status {
  status: string;
  category: ConversationStatusGroup;
}

export interface ConversationMessagesRequest {
  conversationId: string;
  sellerWorkspaceId: string;
}

export interface ConversationMessagesResponse {
  success: boolean;
  data: ConversationMessage[];
  message: string;
}

export enum MsgError {
  REENGAGEMENT_ERROR = 'REENGAGEMENT_ERROR',
}

export interface MsgStats {
  REENGAGEMENT_ERROR: {
    dateTime: string;
    description: string;
  };
  error: {
    dateTime: string;
    type: MsgError;
    description: 'Re-engagement message';
  };
}

export interface ConversationMessage {
  stats: MsgStats;
  pubnubMessage: MessagePayload;
  message?: { text: string; actionBy?: { email: string; firstName: string } };
  timetoken: string;
  customerId: string;
  isInternalNote?: boolean;
  isWidgetChat?: boolean;
  chatId?: string;
}

export interface MessageMediaRequest {
  fileName: string;
  type: string;
  method: 'GET' | 'PUT';
  channelId?: string;
  messageId?: string;
  contentType: string;
  userId?: string;
}

export enum TagsKey {
  ORDER = 'order',
  FINANCIAL = 'financial',
  SUPPORT = 'support',
  PERSONAL = 'personal',
  URGENT = 'urgent',
  CUSTOM = 'custom',
}

export const TAGS_MAP: Record<TagsKey, string[]> = {
  [TagsKey.ORDER]: [
    'create_order',
    'modify_order',
    'cancel_order',
    'order_enquiry',
    'product_enquiry',
    'delivery_enquiry',
    'stock_update',
    'negotiation',
    'discount',
  ],
  [TagsKey.FINANCIAL]: [
    'payment',
    'invoice',
    'ledger',
    'return',
    'credit_note',
    'refund',
  ],
  [TagsKey.SUPPORT]: ['issue', 'support_request', 'query'],
  [TagsKey.PERSONAL]: [],
  [TagsKey.URGENT]: [],
  [TagsKey.CUSTOM]: [],
};

export const TagsIcon: Record<TagsKey, string> = {
  [TagsKey.ORDER]: '📦',
  [TagsKey.FINANCIAL]: '💰',
  [TagsKey.SUPPORT]: '🛠️',
  [TagsKey.PERSONAL]: '✋',
  [TagsKey.URGENT]: '🚨',
  [TagsKey.CUSTOM]: '🖌️',
};

export enum CustomerTypeKey {
  NEW = 'new',
  NEW_LEAD = 'new_lead',
}
export interface GetConversationTagsRequest {
  sellerWorkspaceId: string;
  conversationId: string;
}

export interface GetConversationTagsResponse {
  tags: string[];
  conversationId: string;
  totalCount: number;
}

export interface UpdateConversationTagsRequest {
  tags: string[];
  workspaceId: string;
  conversationId: string;
}

export interface UpdateConversationTagsResponse {
  tags: string[];
  conversationId: string;
  totalCount: number;
}

export interface WaConfigData {
  id: string;
  createdDate: string;
  updatedDate: string;
  workspaceId: string;
  wabaId: string;
  whatsappPhoneNoId: null;
  whatsappPhoneNo: string;
  namespaceId: null;
  metaBusinessToken: null;
  provider: string;
  sid: null;
  qualityRating: string;
  messageLimit: number;
  isDefault: boolean;
  isShared: boolean;
  isDeleted: boolean;
  deletedAt: null;
  status: string;
  displayName: string;
  authCode: null;
  profilePic: null;
  projectId: string;
  isCoexistence: boolean;
  branch: string;
  tag: string;
  esLinkID: string;
  info: {
    step: string;
    message: string;
  };
  isCatalogueSyncEnable: boolean;
}
